/*
 *
 *  * Copyright (c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */
import * as _ from 'underscore';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
  Type,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { ModalChildComponentDirective } from './modal-child-component.directive';
import { Subject, Subscription } from 'rxjs';
import { ModalRef } from './modal-ref';
import { ModalConfig } from './modal-config';
import { MessageService } from '../../services/message.service';
import { DOCUMENT } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-modal',
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ModalComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly _onClose = new Subject<any>();
  private componentRef: ComponentRef<any>;

  childComponentType: Type<any>;

  @ViewChild(ModalChildComponentDirective)
  modalChildComponentDirective: ModalChildComponentDirective;
  @ViewChild('modalContainer') modalContainer: ElementRef<HTMLElement>;
  @ViewChild('modalHeader') modalHeader: ElementRef<HTMLElement>;

  public onClose = this._onClose.asObservable();

  private subscribers: Array<Subscription>;

  private window: Window;
  private parentContainer: HTMLElement;

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    public config: ModalConfig,
    private changeDetectorRef: ChangeDetectorRef,
    private modalRef: ModalRef,
    private messageService: MessageService,
    private renderer: Renderer2,
    private translate: TranslateService,
    @Inject(DOCUMENT) private document: any
  ) {
    this.window = (this.document as Document).defaultView;
  }

  onOverlayClicked() {
    this.modalRef.triggerOutsideClick();
  }

  closeModal(evt, response?) {
    evt.stopPropagation();
    if (this.config.showChkBox) {
      this.config.result = response;
      this.modalRef.close(this.config);
    } else {
      this.modalRef.close(response);
    }
  }

  setVisibility(visible: boolean) {
    if (this.parentContainer) {
      this.renderer.setStyle(this.parentContainer, 'visibility', `${visible ? 'visible' : 'hidden'}`);
    }
  }

  onModalClicked(evt) {
    evt.stopPropagation();
  }

  loadChildComponent(componentType: Type<any>) {
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(componentType);

    const viewContainerRef: ViewContainerRef = this.modalChildComponentDirective.viewContainerRef;
    viewContainerRef.clear();

    this.componentRef = viewContainerRef.createComponent(componentFactory);
  }

  close() {
    this._onClose.next();
  }

  ngOnInit(): void {
    this.subscribers = [];
    if (!this.config.isDecisionModal) {
      if (_.isUndefined(this.config.noButtonLabel)) {
        this.config.noButtonLabel = this.translate.instant('label.OK');
      }
    } else {
      if (_.isUndefined(this.config.noButtonLabel)) {
        this.config.noButtonLabel = this.translate.instant('label.no');
      }
      if (_.isUndefined(this.config.yesButtonLabel)) {
        this.config.yesButtonLabel = this.translate.instant('label.yes');
      }
    }

    if (this.config.isBlockModal) {
      this.subscribers.push(
        this.messageService.on('system.unhealthy').subscribe(data => {
          this.config.disableNoBtn = false;
          this.config.content = data;
        })
      );

      this.subscribers.push(
        this.messageService.on('clear.notification').subscribe(() => {
          this.modalRef.close('YES');
        })
      );
    }

    this.subscribers.push(
      this.messageService.on('close.modalRef').subscribe(() => {
        this.modalRef.close();
      })
    );
  }

  ngAfterViewInit() {
    if (this.config.contentType === 'component') {
      this.loadChildComponent(this.childComponentType);
      this.changeDetectorRef.detectChanges();
    }
    this.modalRef.opened();
    this.parentContainer = this.modalContainer.nativeElement.parentElement;
  }

  ngOnDestroy() {
    if (this.config.isBlockModal && this.subscribers) {
      for (const subscriber of this.subscribers) {
        subscriber.unsubscribe();
      }
    }
    if (this.componentRef) {
      this.componentRef.destroy();
    }
  }

  resized() {
    const height = this.window.innerHeight - 50;
    if (this.modalHeader) {
      const headerHeight = this.modalHeader?.nativeElement?.offsetHeight
        ? this.modalHeader?.nativeElement?.offsetHeight
        : 0;
      this.renderer.setStyle(this.modalContainer.nativeElement, 'max-height', `${height}px`);
      if (this.componentRef?.location) {
        this.renderer.setStyle(this.componentRef.location.nativeElement, 'max-height', `${height - headerHeight}px`);
      }
    }
  }
}
